import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Product } from "../types";

export interface CartItem {
  product: Product;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  addItem: (product: Product) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
}

const STORAGE_KEY = "holyman-cart-v1";
const CartContext = createContext<CartContextValue | null>(null);

function readCart(): CartItem[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(item => item?.product?.id && Number(item?.quantity) > 0);
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readCart);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo<CartContextValue>(() => ({
    items,
    itemCount: items.reduce((total, item) => total + item.quantity, 0),
    addItem(product) {
      setItems(current => {
        const existing = current.find(item => item.product.id === product.id);
        if (!existing) return [...current, { product, quantity: 1 }];
        return current.map(item => item.product.id === product.id
          ? { ...item, quantity: Math.min(100000, item.quantity + 1) }
          : item);
      });
    },
    updateQuantity(productId, quantity) {
      const safeQuantity = Math.max(1, Math.min(100000, Math.trunc(quantity || 1)));
      setItems(current => current.map(item => item.product.id === productId
        ? { ...item, quantity: safeQuantity }
        : item));
    },
    removeItem(productId) {
      setItems(current => current.filter(item => item.product.id !== productId));
    },
    clearCart() {
      setItems([]);
    }
  }), [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart deve ser usado dentro de CartProvider");
  return context;
}
